// ============================================================================
// Explicit authenticated boot-release ledger
// ============================================================================
//
// A surface becomes eligible to bypass legacy boot only by appearing HERE in a
// reviewed release entry. This module intentionally imports no manifest,
// catalog, resource declaration, or ownership data: localized data ownership
// and boot independence are separate facts.
//
// Stage 2 establishes the seam with no releases. A later release must add one
// complete entry and its behavioral proof in the same change; an unrecognized
// or malformed entry invalidates the whole supplied ledger rather than making
// a best-effort partial decision.
// ============================================================================

export type BootRelease = Readonly<{
  /** Authenticated surface id, for example `cattle.herds.index`. */
  surface: string;
  /** Stable, human-reviewable release identifier; never inferred at runtime. */
  releaseId: string;
}>;

export type BootReleaseLedger = readonly BootRelease[];

export type BootReleaseLedgerValidation =
  | Readonly<{valid: true; releasedSurfaces: ReadonlySet<string>}>
  | Readonly<{valid: false}>;

// This stays deliberately independent of migrated-surface declarations and
// catalog keys. Every row below is a separately reviewed exception: its
// renderer, route-resource readiness, transition fencing, and release receipt
// land in the same slice. Do not add a row merely because a surface has
// `owner: permanent`: ownership is necessary, not a boot-release grant.
export const BOOT_RELEASE_LEDGER: BootReleaseLedger = Object.freeze([
  Object.freeze({surface: 'view.cattlebreeding', releaseId: 'cattle-breeding-surface-v1'}),
  Object.freeze({surface: 'view.mySubmissions', releaseId: 'my-submissions-surface-v1'}),
  Object.freeze({surface: 'view.fuelSupply', releaseId: 'fuel-supply-surface-v1'}),
  Object.freeze({surface: 'equipment.fueling.supply', releaseId: 'equipment-fueling-supply-surface-v1'}),
  Object.freeze({surface: 'equipment.fueling.hub', releaseId: 'equipment-fueling-hub-surface-v1'}),
  Object.freeze({surface: 'view.activity', releaseId: 'activity-surface-v1'}),
  Object.freeze({surface: 'view.addfeed', releaseId: 'add-feed-surface-v1'}),
  Object.freeze({surface: 'view.newsletterAdmin', releaseId: 'newsletter-admin-surface-v1'}),
  Object.freeze({surface: 'view.cattlelog', releaseId: 'cattle-log-surface-v1'}),
  Object.freeze({surface: 'view.processing', releaseId: 'processing-surface-v1'}),
  Object.freeze({surface: 'view.production', releaseId: 'production-surface-v1'}),
  Object.freeze({surface: 'view.cattleHome', releaseId: 'cattle-home-surface-v1'}),
  Object.freeze({surface: 'view.sheepHome', releaseId: 'sheep-home-surface-v1'}),
  Object.freeze({surface: 'view.weather', releaseId: 'weather-surface-v1'}),
  Object.freeze({surface: 'cattle.batches.detail', releaseId: 'cattle-batches-detail-surface-v1'}),
  Object.freeze({surface: 'view.cattleforecast', releaseId: 'cattle-forecast-surface-v1'}),
  Object.freeze({surface: 'cattle.dailys.index', releaseId: 'cattle-dailys-index-surface-v1'}),
  Object.freeze({surface: 'layer.batches.index', releaseId: 'layer-batches-index-surface-v1'}),
  Object.freeze({surface: 'equipment.fleet.index', releaseId: 'equipment-fleet-index-surface-v1'}),
  Object.freeze({surface: 'equipment.fleet.detail', releaseId: 'equipment-fleet-detail-surface-v1'}),
  Object.freeze({surface: 'equipment.fueling.form', releaseId: 'equipment-fueling-form-surface-v1'}),
  Object.freeze({surface: 'cattle.dailys.detail', releaseId: 'cattle-dailys-detail-surface-v1'}),
  Object.freeze({
    surface: 'cattle.batches.forecastDetail',
    releaseId: 'cattle-batches-forecast-detail-surface-v1',
  }),
  Object.freeze({surface: 'app.home.light', releaseId: 'light-home-surface-v1'}),
  Object.freeze({surface: 'app.home.standard', releaseId: 'standard-home-surface-v1'}),
  Object.freeze({surface: 'view.broilerweighins', releaseId: 'broiler-weighins-surface-v1'}),
  Object.freeze({surface: 'view.pigweighins', releaseId: 'pig-weighins-surface-v1'}),
  Object.freeze({surface: 'view.cattleweighins', releaseId: 'cattle-weighins-surface-v1'}),
  Object.freeze({surface: 'view.sheepweighins', releaseId: 'sheep-weighins-surface-v1'}),
  Object.freeze({surface: 'cattle.herds.index', releaseId: 'cattle-herds-index-surface-v1'}),
  Object.freeze({surface: 'cattle.herds.detail', releaseId: 'cattle-herds-detail-surface-v1'}),
  Object.freeze({surface: 'sheep.batches.index', releaseId: 'sheep-batches-index-surface-v1'}),
  Object.freeze({surface: 'sheep.batches.detail', releaseId: 'sheep-batches-detail-surface-v1'}),
  Object.freeze({surface: 'sheep.flocks.index', releaseId: 'sheep-flocks-index-surface-v1'}),
  Object.freeze({surface: 'sheep.flocks.detail', releaseId: 'sheep-flocks-detail-surface-v1'}),
  Object.freeze({surface: 'sheep.dailys.detail', releaseId: 'sheep-dailys-detail-surface-v1'}),
  Object.freeze({surface: 'tasks.center', releaseId: 'tasks-center-surface-v1'}),
  Object.freeze({surface: 'cattle.batches.index', releaseId: 'cattle-batches-index-surface-v1'}),
  Object.freeze({surface: 'tasks.todo.detail', releaseId: 'tasks-todo-detail-surface-v1'}),
  Object.freeze({surface: 'tasks.instance.detail', releaseId: 'tasks-instance-detail-surface-v1'}),
  Object.freeze({surface: 'equipment.fueling.entry', releaseId: 'equipment-fueling-entry-surface-v1'}),
  Object.freeze({surface: 'equipment.checklist.entry', releaseId: 'equipment-checklist-entry-surface-v1'}),
  Object.freeze({surface: 'layer.dailys.index', releaseId: 'layer-dailys-index-surface-v1'}),
  Object.freeze({surface: 'layer.dailys.detail', releaseId: 'layer-dailys-detail-surface-v1'}),
  Object.freeze({surface: 'layer.housings.detail', releaseId: 'layer-housings-detail-surface-v1'}),
  Object.freeze({surface: 'layer.batches.detail', releaseId: 'layer-batches-detail-surface-v1'}),
  Object.freeze({surface: 'layer.eggDailys.index', releaseId: 'layer-egg-dailys-index-surface-v1'}),
  Object.freeze({surface: 'layer.eggDailys.detail', releaseId: 'layer-egg-dailys-detail-surface-v1'}),
  Object.freeze({surface: 'pig.dailys.index', releaseId: 'pig-dailys-index-surface-v1'}),
  Object.freeze({surface: 'pig.dailys.detail', releaseId: 'pig-dailys-detail-surface-v1'}),
  Object.freeze({surface: 'pig.batches.index', releaseId: 'pig-batches-index-surface-v1'}),
  Object.freeze({surface: 'pig.batches.detail', releaseId: 'pig-batches-detail-surface-v1'}),
  Object.freeze({surface: 'sheep.dailys.index', releaseId: 'sheep-dailys-index-surface-v1'}),
  Object.freeze({surface: 'broiler.dailys.index', releaseId: 'broiler-dailys-index-surface-v1'}),
  Object.freeze({surface: 'broiler.dailys.detail', releaseId: 'broiler-dailys-detail-surface-v1'}),
  Object.freeze({surface: 'view.broilerHome', releaseId: 'broiler-home-surface-v1'}),
  Object.freeze({surface: 'view.layersHome', releaseId: 'layer-home-surface-v1'}),
  Object.freeze({surface: 'view.layers', releaseId: 'layer-groups-surface-v1'}),
  Object.freeze({surface: 'view.pigsHome', releaseId: 'pig-home-surface-v1'}),
  Object.freeze({surface: 'view.animalHistory', releaseId: 'animal-history-surface-v1'}),
  Object.freeze({surface: 'view.weighins', releaseId: 'weighins-webform-surface-v1'}),
  Object.freeze({surface: 'view.weighinsessions', releaseId: 'weighin-session-record-surface-v1'}),
  Object.freeze({surface: 'view.clientErrors', releaseId: 'client-errors-surface-v1'}),
  Object.freeze({surface: 'view.webforms', releaseId: 'webforms-admin-surface-v1'}),
  Object.freeze({surface: 'view.webform', releaseId: 'pig-dailys-webform-surface-v1'}),
  Object.freeze({surface: 'view.webformhub', releaseId: 'webform-hub-surface-v1'}),
  Object.freeze({surface: 'view.pastureMap', releaseId: 'pasture-map-surface-v1'}),
  Object.freeze({surface: 'view.breeding', releaseId: 'pig-breeding-timeline-surface-v1'}),
  Object.freeze({surface: 'view.farrowing', releaseId: 'pig-farrowing-surface-v1'}),
  Object.freeze({surface: 'view.sows', releaseId: 'pig-sows-surface-v1'}),
  Object.freeze({surface: 'view.tasksWebform', releaseId: 'tasks-webform-surface-v1'}),
  Object.freeze({surface: 'view.timeline', releaseId: 'broiler-timeline-surface-v1'}),
  Object.freeze({surface: 'view.feed', releaseId: 'broiler-feed-surface-v1'}),
  Object.freeze({surface: 'view.pigs', releaseId: 'pig-feed-surface-v1'}),
  Object.freeze({surface: 'broiler.batches.index', releaseId: 'broiler-batches-index-surface-v1'}),
  Object.freeze({surface: 'broiler.batches.detail', releaseId: 'broiler-batches-detail-surface-v1'}),
  Object.freeze({surface: 'broiler.batchForm.fullscreen', releaseId: 'broiler-batch-form-fullscreen-surface-v1'}),
  Object.freeze({surface: 'view.processingCutSheet', releaseId: 'processing-cut-sheet-surface-v1'}),
  Object.freeze({surface: 'view.equipmentAdminRecord', releaseId: 'equipment-admin-record-surface-v1'}),
  Object.freeze({surface: 'view.projects', releaseId: 'projects-surface-v1'}),
]);

function isReleaseEntry(value: unknown): value is BootRelease {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entry = value as Record<string, unknown>;
  const keys = Object.keys(entry).sort();
  return (
    keys.length === 2 &&
    keys[0] === 'releaseId' &&
    keys[1] === 'surface' &&
    typeof entry.surface === 'string' &&
    entry.surface.trim() !== '' &&
    typeof entry.releaseId === 'string' &&
    entry.releaseId.trim() !== ''
  );
}

/**
 * Validate an explicitly supplied ledger against the current authenticated
 * topology. Invalid input is deliberately all-or-nothing: accepting the rows
 * that happened to parse would make a typo or duplicate release a silent
 * authorization decision.
 */
export function validateBootReleaseLedger(
  ledger: unknown,
  knownSurfaceIds: ReadonlySet<string>,
): BootReleaseLedgerValidation {
  if (!Array.isArray(ledger)) return Object.freeze({valid: false});

  const releasedSurfaces = new Set<string>();
  const releaseIds = new Set<string>();
  for (const entry of ledger) {
    if (!isReleaseEntry(entry)) return Object.freeze({valid: false});
    if (!knownSurfaceIds.has(entry.surface)) return Object.freeze({valid: false});
    if (releasedSurfaces.has(entry.surface) || releaseIds.has(entry.releaseId)) {
      return Object.freeze({valid: false});
    }
    releasedSurfaces.add(entry.surface);
    releaseIds.add(entry.releaseId);
  }

  return Object.freeze({valid: true, releasedSurfaces});
}
